import React from 'react';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { useQuery } from '@apollo/client';
import { Box, Flex, Text } from 'rebass';
import { useHistory } from 'react-router-dom';
import useMode from '../../hooks/useMode';
import { useStoreFilters } from '../../hooks/useStoredFilters';
import {
  WaterServiceFilters,
  defaultFilters,
} from '../../modes/waterSupplier/pages/waterServices/FilterBarWaterServices';
import {
  hazardProtectionStatusStyle,
  HazardProtectionStatus,
} from '../WaterService/HazardProtectionStatusPicker';
import { ucWords } from '@ecosconnect/core/src/helpers/string';
import { gql } from '@/modes/waterSupplier/gql';

const HAZARD_PROTECTION_STATUS_QUERY = gql(`
  query HazardProtectionStatusBreakdown {
    statusBreakdown: waterServices(
      aggregate: [{ col: "id", type: DISTINCT, as: "waterServices" }]
      groupBy: ["hazardProtectionStatus"]
      orderBy: [{ col: "agg.waterServices", dir: DESC }]
      limit: null
    ) {
      hazardProtectionStatus
      agg
    }
  }
`);

type StatusBreakdownData = {
  statusBreakdown: {
    hazardProtectionStatus: HazardProtectionStatus;
    agg: {
      waterServices: number;
    };
  }[];
};

type HazardProtectionStatusWidgetProps = {
  width: number;
  height: number;
};

const HazardProtectionStatusWidget = ({
  width,
  height,
}: HazardProtectionStatusWidgetProps): JSX.Element => {
  const history = useHistory();
  const mode = useMode();
  const storeFilters = useStoreFilters<WaterServiceFilters>('waterServices');

  const { data } = useQuery<StatusBreakdownData>(
    HAZARD_PROTECTION_STATUS_QUERY,
    { fetchPolicy: 'no-cache' }
  );

  const max = data
    ? Math.max(...data.statusBreakdown.map((row) => row.agg.waterServices))
    : 0;

  return (
    <>
      {data && width && (
        <Flex flexDirection="column" flexGrow={1} overflowY="auto">
          {data.statusBreakdown.map((row) => (
            <Flex
              height="20%"
              minHeight="35px"
              flexShrink={0}
              justifyContent="center"
              key={row.hazardProtectionStatus}
              flexDirection="column"
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
              onClick={() => {
                storeFilters({
                  ...defaultFilters,
                  hazardProtectionStatus: [row.hazardProtectionStatus],
                });
                history.push(`/${mode}/water-services`);
              }}
            >
              <Flex justifyContent="space-between" mb={1}>
                <Text variant="body2">
                  {constantToLabel(row.hazardProtectionStatus)}
                </Text>
                <Text variant="body2" pr={1} fontWeight="bold">
                  {row.agg.waterServices}
                </Text>
              </Flex>
              <Box
                sx={{
                  borderRadius: 'circle',
                  height: 12,
                  bg:
                    'status' +
                    ucWords(
                      hazardProtectionStatusStyle(row.hazardProtectionStatus)
                    ),
                  width:
                    max > 0 ? (row.agg.waterServices / max) * 100 + '%' : '0%',
                }}
              />
            </Flex>
          ))}
        </Flex>
      )}
    </>
  );
};

export default HazardProtectionStatusWidget;
